import React, { useEffect, useState, useCallback } from 'react';
import {
  Box, Typography, Table, TableHead, TableRow, TableCell, TableBody,
  TextField, IconButton, Tooltip, Collapse, Chip,
} from '@mui/material';
import SaveIcon from '@mui/icons-material/Save';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import { useTranslation } from 'react-i18next';
import myDriveService, { TenantQuota, UserQuota } from '../services/myDriveService';
import { useMessage } from '../../../../contexts/MessageContext';
import { usePermission } from '../../../../hooks/usePermission';

// ─── UserQuotaTable（展開後顯示）────────────────────────────────────

function UserQuotaTable({ orgId, canUpdate }: { orgId: number; canUpdate: boolean }) {
  const { t } = useTranslation();
  const { showSuccess, showApiError } = useMessage();
  const [users, setUsers] = useState<UserQuota[]>([]);
  const [edits, setEdits] = useState<Record<number, string>>({});

  useEffect(() => {
    myDriveService.listOrgUsers(orgId).then(setUsers).catch(showApiError);
  }, [orgId, showApiError]);

  const save = useCallback(async (userId: number) => {
    const raw = edits[userId];
    const quota_gb = raw === '' || raw === undefined ? null : parseFloat(raw);
    if (quota_gb !== null && (isNaN(quota_gb) || quota_gb <= 0)) return;
    try {
      const updated = await myDriveService.updateUserQuota(userId, quota_gb);
      setUsers(prev => prev.map(u => u.user_id === userId ? updated : u));
      setEdits(prev => { const n = { ...prev }; delete n[userId]; return n; });
      showSuccess('SYS330004', { id: String(userId), quota_gb: String(quota_gb ?? '繼承') });
    } catch (err) {
      showApiError(err);
    }
  }, [edits, showSuccess, showApiError]);

  return (
    <Table size="small" sx={{ ml: 4 }}>
      <TableHead>
        <TableRow sx={{ bgcolor: 'grey.100' }}>
          <TableCell>{t('myDrive.userAccount', '帳號')}</TableCell>
          <TableCell>{t('myDrive.userName', '姓名')}</TableCell>
          <TableCell>{t('myDrive.userQuota', '個人配額 (GB)')}
            <Typography variant="caption" color="text.secondary">
              {t('myDrive.inherit', '（空白 = 繼承租戶）')}
            </Typography>
          </TableCell>
          <TableCell>{t('myDrive.userUsed', '已使用')}</TableCell>
          {canUpdate && <TableCell>{t('myDrive.save', '儲存')}</TableCell>}
        </TableRow>
      </TableHead>
      <TableBody>
        {users.map(u => {
          const currentVal = edits[u.user_id] !== undefined
            ? edits[u.user_id]
            : (u.quota_gb !== null ? String(u.quota_gb) : '');
          return (
            <TableRow key={u.user_id} hover>
              <TableCell>{u.account}</TableCell>
              <TableCell>{u.username}</TableCell>
              <TableCell>
                <TextField
                  size="small"
                  type="number"
                  placeholder={t('myDrive.inherit', '繼承')}
                  value={currentVal}
                  onChange={e => setEdits(prev => ({ ...prev, [u.user_id]: e.target.value }))}
                  inputProps={{ min: 1, step: 1 }}
                  sx={{ width: 100 }}
                  disabled={!canUpdate}
                />
                <Typography variant="caption" sx={{ ml: 0.5 }}>GB</Typography>
              </TableCell>
              <TableCell>
                <Chip size="small" label={`${u.used_gb} GB`}
                      color={u.quota_gb !== null && u.used_gb >= u.quota_gb ? 'error' : 'default'} />
              </TableCell>
              {canUpdate && (
                <TableCell>
                  <Tooltip title={t('myDrive.save', '儲存')}>
                    <IconButton size="small" color="primary" onClick={() => save(u.user_id)}>
                      <SaveIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </TableCell>
              )}
            </TableRow>
          );
        })}
      </TableBody>
    </Table>
  );
}

// ─── TenantQuotaPage ─────────────────────────────────────────────

export default function TenantQuotaPage() {
  const { t } = useTranslation();
  const { showSuccess, showApiError } = useMessage();
  const { hasPermission, loading: permLoading } = usePermission();
  const canRead   = hasPermission('ee_mod_my_drive', 'read');
  const canUpdate = hasPermission('ee_mod_my_drive', 'update');
  const [quotas, setQuotas] = useState<TenantQuota[]>([]);
  const [edits, setEdits] = useState<Record<number, string>>({});
  const [expanded, setExpanded] = useState<Set<number>>(new Set());

  useEffect(() => {
    myDriveService.listTenantQuotas().then(setQuotas).catch(showApiError);
  }, [showApiError]);

  const saveTenant = async (org_id: number) => {
    const gb = parseFloat(edits[org_id] ?? '');
    if (!gb || gb <= 0) return;
    try {
      const updated = await myDriveService.updateTenantQuota(org_id, gb);
      setQuotas(prev => prev.map(q => q.organization_id === org_id ? updated : q));
      showSuccess('SYS330004', { id: String(org_id), quota_gb: String(gb) });
    } catch (err) {
      showApiError(err);
    }
  };

  const toggleExpand = (orgId: number) =>
    setExpanded(prev => {
      const next = new Set(prev);
      if (next.has(orgId)) next.delete(orgId); else next.add(orgId);
      return next;
    });

  if (permLoading) return null;

  if (!canRead) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography color="text.secondary">{t('common.noPermission', '沒有檢視權限')}</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h5" gutterBottom>
        {t('myDrive.adminTitle', '網路硬碟管理 — 組織配額')}
      </Typography>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell sx={{ width: 40 }} />
            <TableCell>{t('myDrive.orgId', '組織 ID')}</TableCell>
            <TableCell>{t('myDrive.orgName', '組織名稱')}</TableCell>
            <TableCell>{t('myDrive.userCount', '用戶數')}</TableCell>
            <TableCell>{t('myDrive.currentQuota', '目前配額 (GB)')}</TableCell>
            <TableCell>{t('myDrive.newQuota', '新配額 (GB)')}</TableCell>
            {canUpdate && <TableCell>{t('myDrive.save', '儲存')}</TableCell>}
          </TableRow>
        </TableHead>
        <TableBody>
          {quotas.map(q => (
            <React.Fragment key={q.organization_id}>
              <TableRow hover>
                <TableCell>
                  <IconButton size="small" onClick={() => toggleExpand(q.organization_id)}>
                    {expanded.has(q.organization_id) ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                  </IconButton>
                </TableCell>
                <TableCell>{q.organization_id}</TableCell>
                <TableCell>{q.org_name}</TableCell>
                <TableCell>{q.user_count}</TableCell>
                <TableCell>{q.quota_gb} GB</TableCell>
                <TableCell>
                  <TextField
                    size="small"
                    type="number"
                    inputProps={{ min: 1, step: 1 }}
                    value={edits[q.organization_id] ?? q.quota_gb}
                    onChange={e => setEdits(prev => ({ ...prev, [q.organization_id]: e.target.value }))}
                    sx={{ width: 120 }}
                    disabled={!canUpdate}
                  />
                </TableCell>
                {canUpdate && (
                  <TableCell>
                    <Tooltip title={t('myDrive.save', '儲存')}>
                      <IconButton size="small" color="primary"
                                  onClick={() => saveTenant(q.organization_id)}>
                        <SaveIcon />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                )}
              </TableRow>

              {/* 展開後顯示個人配額 */}
              <TableRow>
                <TableCell colSpan={canUpdate ? 7 : 6} sx={{ p: 0, borderBottom: 0 }}>
                  <Collapse in={expanded.has(q.organization_id)} timeout="auto" unmountOnExit>
                    <Box sx={{ py: 1, bgcolor: 'grey.50' }}>
                      <UserQuotaTable orgId={q.organization_id} canUpdate={canUpdate} />
                    </Box>
                  </Collapse>
                </TableCell>
              </TableRow>
            </React.Fragment>
          ))}
        </TableBody>
      </Table>
    </Box>
  );
}
